/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';
import { Link as ReactRouterLink } from 'react-router';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';

import { Grid, Typography2 } from '@coursera/cds-core';

import { getProfileDescription } from 'bundles/classmates/components/MiniProfile';
import MiniProfileRenderer from 'bundles/classmates/components/MiniProfileRenderer';
import ClassmatesProfileStore from 'bundles/classmates/stores/ClassmatesProfileStore';
import { getCourseRoleString, getCourseRoleValue } from 'bundles/classmates/utils/lib';
import PillTag from 'bundles/discussions/components/forumsV2-PillTag';
import ProfileAvatar from 'bundles/discussions/components/forumsV2-ProfileAvatar';
import { getProfileLink } from 'bundles/discussions/utils/discussionsUrl';
import { initialsFromName } from 'bundles/discussions/utils/forumsV2-nameParser';
import { decorateRoleLabel, isRoleVerified } from 'bundles/discussions/utils/userUtils';

type Props = {
  courseRole?: string;
  externalUserId: string;
  helperStatus?: string;
  fullName: string;
  profileImageUrl?: string;
  profileLinkId?: string;
};

const styles = {
  miniProfile: css`
    position: absolute;
    z-index: 1;
  `,
};

const ReplyProfileInformation = ({
  courseRole,
  externalUserId,
  fullName,
  helperStatus,
  profileImageUrl,
  profileLinkId,
}: Props) => {
  const { profile } = useFluxibleStore(ClassmatesProfileStore, (store) => {
    return {
      profile: store.profiles.find(({ externalId }) => externalId === externalUserId),
    };
  });

  const [showMini, setShowMini] = useState(false);

  const role = getCourseRoleValue(courseRole, helperStatus);

  const roleString = decorateRoleLabel(role, getCourseRoleString(role));
  const profileDescription = courseRole && profile && getProfileDescription(courseRole, profile);
  const hasRole = roleString !== undefined;
  const isVerifiedRole = isRoleVerified(role);
  const ariaLabel = `Name: ${fullName},
                     ${hasRole ? `Role: ${roleString}, ` : ''}
                     ${!isVerifiedRole && profileDescription ? `Progress: ${profileDescription}, ` : ''}
                     ${profile ? `${profile.postsCount} posts, ` : ''}
                     ${profile ? `${profile.votesReceivedCount} votes` : ''}`;
  const toolTipId = `${externalUserId}-tooltip`;

  // Profile may not be in focus while hovered, so escape needs to be checked globally
  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setShowMini(false);
      }
    };
    document.addEventListener('keydown', onEscape, false);

    return () => {
      document.removeEventListener('keydown', onEscape, false);
    };
  }, []);

  const initials = (fullName && initialsFromName(fullName)) || '--';
  const pillLabel = getCourseRoleString(role);

  return (
    <>
      <ReactRouterLink
        className="pii-hide"
        id={profileLinkId}
        to={getProfileLink(externalUserId)}
        onMouseEnter={() => {
          setShowMini(true);
        }}
        onFocus={() => setShowMini(true)}
        onMouseLeave={() => setShowMini(false)}
        onBlur={() => setShowMini(false)}
        aria-label={ariaLabel}
        role="link"
      >
        <Grid container direction="row" alignItems="center" spacing={12}>
          <Grid item>
            <ProfileAvatar initials={initials} photoUrl={profileImageUrl} name={fullName} />
          </Grid>
          <Grid item>
            <Typography2 component="span" variant="subtitleMedium">
              {fullName}
            </Typography2>
          </Grid>
          {pillLabel && (
            <Grid>
              <PillTag label={pillLabel} badge={role} />
            </Grid>
          )}
        </Grid>
      </ReactRouterLink>

      <span data-js="mini-profile" aria-hidden="true" css={styles.miniProfile}>
        <MiniProfileRenderer
          source="ReplyProfileInformation"
          externalId={externalUserId}
          courseRole={role}
          toolTipId={toolTipId}
          showMini={showMini}
          profileImageUrl={profileImageUrl}
          fullName={fullName}
        />
      </span>
    </>
  );
};

export default ReplyProfileInformation;
